import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Input, Popover } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import {
  PICKER_ICON_KEYS,
  canonicalIconKey
} from '../../trm/smartAssessment/FramlAlertDetailShell/components/eventIcons';
import { iconLabel } from './iconLabel';
import EventIcon from './EventIcon';
import style from './index.scss';

/**
 * §7 — Event Icon Configuration.
 *
 * "Icon picker uses the approved product icon library as per our icon set in
 * FinCense" — settled by the design as the STANDARD LUCIDE SET, which is what it
 * captions itself with. The choices come from PICKER_ICON_KEYS, derived from the
 * same registry the Spatial View draws from. That is the point: an icon offered
 * here cannot fail to render on a node, because both sides read one source. A
 * separate list would let an admin pick a glyph the timeline has never heard of.
 *
 * THIRTY-SIX GLYPHS IN SIX ROWS OF SIX, alphabetical — the design's exact grid.
 * Glyph only, no name beneath: at six columns there is no room for one, and the
 * name is on the trigger button and in the tooltip instead.
 *
 * A POPOVER ANCHORED TO THE TRIGGER, not a modal — same reasoning as
 * AddFieldsModal, and the design's DOM says so outright:
 *   <div role="dialog" data-side="top" data-align="end"
 *        class="z-50 rounded-md border bg-popover shadow-md w-72 p-3">
 * inside a `data-radix-popper-content-wrapper` whose
 * `--radix-popper-anchor-width: 89.890625px` / `--radix-popper-anchor-height:
 * 36px` are the Event-icon button itself. `data-side="top" data-align="end"` is
 * antd's `topRight`. Measured box: 288 wide, 12 padding, 4 radius — see
 * .iconPopover.
 *
 * TAKES THE TRIGGER AS `children` so the anchor and the panel are one element.
 * The open state stays with the screen, which owns the selected type.
 *
 * "Administrator can search icons" — by lucide key ("log-in") or by the name
 * shown ("LogIn").
 *
 * NO "DEFAULT" CHOICE, matching the design: the panel is the thirty-six and
 * nothing else. §7's "if no icon is configured, the system must use the default
 * Activity or Transaction icon" still holds for a type that has never had one
 * (`icon_key` is nullable and EventIcon falls back), but there is now NO WAY
 * BACK — once an admin picks a glyph, this panel cannot return the type to the
 * category default. The design has no affordance for it either. Raised rather
 * than worked around; if it needs one it belongs in the edit-type modal beside
 * the other type-level fields, not as a thirty-seventh cell that would push the
 * alphabetical grid out of shape.
 *
 * "One icon can be configured per event type" — picking closes immediately;
 * there is no multi-select and no confirm step.
 */
const IconPicker = ({ open, current, onPick, onCancel, children }) => {
  const [term, setTerm] = useState('');

  // NORMALISED, because `current` comes straight off the server row and may be a
  // PRE-REKEY key. A row holding `desktop` compared against a grid keyed
  // `monitor` matched nothing, so opening the picker on it showed no selection
  // at all — the admin could not see what was already chosen.
  const currentKey = canonicalIconKey(current);

  const results = useMemo(() => {
    const keys = PICKER_ICON_KEYS;
    const q = term.trim().toLowerCase();
    if (!q) return keys;
    return keys.filter(
      (k) =>
        k.toLowerCase().includes(q) || iconLabel(k).toLowerCase().includes(q)
    );
  }, [term]);

  const choose = (iconKey) => {
    setTerm('');
    onPick(iconKey);
  };

  const panel = (
    <div className={style.iconPickerBody}>
      <Input
        allowClear
        value={term}
        onChange={(e) => setTerm(e.target.value)}
        prefix={<SearchOutlined />}
        placeholder="Search icons..."
        aria-label="Search icons"
      />

      <div className={style.iconGrid}>
        {results.map((key) => (
          <button
            type="button"
            key={key}
            className={`${style.iconChoice} ${
              currentKey === key ? style.iconChoiceActive : ''
            }`}
            onClick={() => choose(key)}
            aria-label={iconLabel(key)}
            title={iconLabel(key)}
          >
            <EventIcon iconKey={key} />
          </button>
        ))}
      </div>

      {results.length === 0 && (
        <p className={style.iconGridEmpty}>No icon matches “{term.trim()}”.</p>
      )}

      {/* Verbatim from the design — it names the library so an admin can look a
          glyph up rather than guess from a 16px swatch. */}
      <p className={style.iconGridCaption}>
        Standard Lucide icon set (lucide.dev).
      </p>
    </div>
  );

  return (
    <Popover
      overlayClassName={style.iconPopover}
      content={panel}
      open={open}
      onOpenChange={(next) => {
        if (next) return;
        setTerm('');
        onCancel();
      }}
      trigger="click"
      /* `data-side="top" data-align="end"` in the design's DOM. */
      placement="topRight"
      destroyTooltipOnHide
    >
      {children}
    </Popover>
  );
};

IconPicker.propTypes = {
  open: PropTypes.bool,
  /** The key currently saved on the type, or null for the category default. */
  current: PropTypes.string,
  onPick: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  /** The trigger the popover anchors to. */
  children: PropTypes.node
};

export default IconPicker;
